/**
 * Content-aware resume pagination — frontend copy.
 *
 * `paginateResumeDocument` / `unwrapResumeDocument` are duplicated from
 * pdf-generator-playwright/paginateResume.js so the preview computes the exact
 * same page breaks as the PDF generator. KEEP THE TWO COPIES IN SYNC.
 *
 * This module adds browser helpers:
 *   - paginateHtml(html): paginate a full HTML document string in a hidden iframe
 *   - extractPages(html): split a paginated document into per-page display fragments
 *   - replacePageInHtml(fullHtml, index, fragment): merge an edited page back
 */

export function paginateResumeDocument(doc, win) {
  var TOL = 4; // px tolerance for boundary comparisons
  var MAX_PAGES = 15;

  var ITEM_SEL =
    '.experience-item, .timeline-item, .education-item, .edu-item, .exp-item, ' +
    '.proj-item, .project-item, .cert-item, .skill-category, .skills-box, .skill-row';
  var ATOMIC_SEL =
    '.header, .header-banner, .summary-section, .skills-grid, .achievements, ' +
    'ul, ol, p, img, table, h1, h2, h3, h4';
  var TITLE_SEL = '.section-title, h1, h2, h3';
  // Only real section titles are repeated as "(continued)" headers; h1 (the
  // person's name) and other headings must never be duplicated.
  var CONT_TITLE_SEL = '.section-title';
  var PARALLEL_SEL = '.split-container, .two-col';

  var splitIdCounter = 0;

  function injectStyles() {
    if (doc.querySelector('style[data-resume-pagination]')) return;
    var style = doc.createElement('style');
    style.setAttribute('data-resume-pagination', 'true');
    style.textContent =
      '.left-column-full, .right-column-full {' +
      ' flex: 1 1 100% !important; max-width: 100% !important; width: 100% !important;' +
      ' padding-left: 0 !important; padding-right: 0 !important;' +
      ' border-right: none !important; border-left: none !important; }' +
      '@media print {' +
      ' html, body { margin: 0 !important; padding: 0 !important; background: white !important; }' +
      ' .resume-page { margin: 0 !important; box-shadow: none !important;' +
      '  page-break-after: always !important; break-after: page !important; }' +
      ' .resume-page:last-child { page-break-after: auto !important; break-after: auto !important; }' +
      ' .resume-page[data-page] { height: 297mm !important; min-height: 297mm !important;' +
      '  max-height: 297mm !important; overflow: hidden !important; }' +
      '}';
    doc.head.appendChild(style);
  }

  function measurePageHeight() {
    var probe = doc.createElement('div');
    probe.style.cssText =
      'position:absolute;visibility:hidden;height:297mm;width:10mm;padding:0;border:0;margin:0;';
    doc.body.appendChild(probe);
    var h = probe.getBoundingClientRect().height;
    doc.body.removeChild(probe);
    return h;
  }

  function isTitle(el) {
    return el.matches(TITLE_SEL);
  }

  function contentChildren(el) {
    return Array.prototype.filter.call(el.children, function (c) {
      if (isTitle(c)) return false;
      var cs = win.getComputedStyle(c);
      if (cs.position === 'absolute' || cs.position === 'fixed') return false;
      if (cs.display === 'none') return false;
      return true;
    });
  }

  function isParallel(el) {
    return el.matches(PARALLEL_SEL);
  }

  function isPageParallel(pageEl) {
    return !!(
      pageEl.querySelector(':scope > .left-column') &&
      pageEl.querySelector(':scope > .right-column')
    );
  }

  function isSplittable(el) {
    if (el.matches(ITEM_SEL)) return false;
    if (el.matches(ATOMIC_SEL)) return false;
    var kids = contentChildren(el);
    if (!kids.length) return false;
    if (el.matches('.section, .timeline, .content, .left-column, .right-column')) return true;
    // Unclassed wrappers are splittable only when they contain recognizable content.
    if (el.querySelector(ITEM_SEL) || el.querySelector('.section')) return true;
    return false;
  }

  function getFlowRoot(pageEl) {
    return pageEl.querySelector(':scope > .content') || pageEl;
  }

  function pageBottomLimit(pageEl, pageHeightPx) {
    var rect = pageEl.getBoundingClientRect();
    var padBottom = parseFloat(win.getComputedStyle(pageEl).paddingBottom) || 0;
    var flow = getFlowRoot(pageEl);
    if (flow !== pageEl) {
      padBottom = Math.max(padBottom, parseFloat(win.getComputedStyle(flow).paddingBottom) || 0);
    }
    return rect.top + pageHeightPx - padBottom;
  }

  function bottomChrome(el) {
    var cs = win.getComputedStyle(el);
    return (parseFloat(cs.marginBottom) || 0) + (parseFloat(cs.paddingBottom) || 0);
  }

  /**
   * Bottom margin of `el`, including child margins that collapse through it
   * (an element with no bottom padding/border exposes its last child's margin
   * below its own border box, which getBoundingClientRect doesn't show).
   */
  function effectiveMarginBottom(el, depth) {
    var cs = win.getComputedStyle(el);
    var mb = parseFloat(cs.marginBottom) || 0;
    if (depth >= 4) return mb;
    var canCollapse =
      (parseFloat(cs.paddingBottom) || 0) === 0 &&
      (parseFloat(cs.borderBottomWidth) || 0) === 0 &&
      cs.display !== 'flex' &&
      cs.display !== 'grid' &&
      cs.overflow === 'visible';
    if (canCollapse) {
      var kids = el.children;
      for (var i = kids.length - 1; i >= 0; i--) {
        var kcs = win.getComputedStyle(kids[i]);
        if (kcs.display === 'none' || kcs.position === 'absolute' || kcs.position === 'fixed') {
          continue;
        }
        if (kcs.float === 'none' && kcs.position !== 'sticky') {
          mb = Math.max(mb, effectiveMarginBottom(kids[i], depth + 1));
        }
        break;
      }
    }
    return mb;
  }

  function planColumns(colEls, limit, extraChrome) {
    return colEls.map(function (col) {
      return { el: col, plan: planContainer(col, limit, extraChrome + bottomChrome(col)) };
    });
  }

  /**
   * Plan which children of `container` must move past `limit`.
   * Does not mutate the DOM. Returns { moves, fullMove }.
   * Move kinds: whole | partial (with sub-plan) | parallel (per-column plans).
   *
   * `chrome` is the accumulated bottom margin/padding of enclosing containers:
   * when an element becomes the last one on a page, its ancestors' bottom
   * chrome stacks below it and must still fit above the page boundary.
   */
  function planContainer(container, limit, chrome) {
    var kids = contentChildren(container);
    var moves = [];
    var firstKid = kids.length ? kids[0] : null;

    for (var i = 0; i < kids.length; i++) {
      var kid = kids[i];
      var r = kid.getBoundingClientRect();
      if (r.height === 0) continue;

      var effBottom = r.bottom + effectiveMarginBottom(kid, 0) + chrome;

      if (r.top >= limit - TOL) {
        moves.push({ kind: 'whole', el: kid });
      } else if (effBottom > limit + TOL) {
        if (isParallel(kid)) {
          var cols = planColumns(contentChildren(kid), limit, chrome + bottomChrome(kid));
          var hasColMoves = cols.some(function (c) {
            return c.plan.moves.length > 0;
          });
          if (hasColMoves) moves.push({ kind: 'parallel', el: kid, cols: cols });
        } else if (isSplittable(kid)) {
          var sub = planContainer(kid, limit, chrome + bottomChrome(kid));
          if (sub.fullMove) {
            // Every unit inside wants to move: move the container whole so the
            // title travels with its content instead of dangling.
            moves.push({ kind: 'whole', el: kid });
          } else if (sub.moves.length) {
            moves.push({ kind: 'partial', el: kid, plan: sub });
          }
        } else {
          moves.push({ kind: 'whole', el: kid });
        }
      }
    }

    var fullMove = false;
    if (moves.length && firstKid && moves[0].el === firstKid) {
      var m0 = moves[0];
      if (m0.kind === 'whole') {
        fullMove = true;
      } else if (m0.kind === 'parallel') {
        fullMove = m0.cols.every(function (c) {
          return c.plan.moves.length > 0 && c.plan.fullMove;
        });
      }
    }
    return { moves: moves, fullMove: fullMove };
  }

  /**
   * A plan that would empty the current page cannot make progress (the moved
   * content would overflow the next page identically, forever). Force the
   * first leaf unit to stay on the current page. Only invoked when the page
   * would otherwise end up empty.
   */
  function pinFirstLeaf(plan) {
    if (!plan.moves.length) return;
    var m = plan.moves[0];
    if (m.kind === 'partial') {
      pinFirstLeaf(m.plan);
      if (!m.plan.moves.length) plan.moves.shift();
      return;
    }
    if (m.kind === 'parallel') {
      for (var i = 0; i < m.cols.length; i++) {
        if (m.cols[i].plan.moves.length) {
          pinFirstLeaf(m.cols[i].plan);
          break;
        }
      }
      var allEmpty = m.cols.every(function (c) {
        return !c.plan.moves.length;
      });
      if (allEmpty) plan.moves.shift();
      return;
    }
    // 'whole': keep the element's first unit by converting to a partial move,
    // or keep the whole atomic element (giant-element fallback).
    if (isSplittable(m.el)) {
      var kids = contentChildren(m.el);
      var subMoves = kids.slice(1).map(function (k) {
        return { kind: 'whole', el: k };
      });
      if (subMoves.length) {
        plan.moves[0] = { kind: 'partial', el: m.el, plan: { moves: subMoves, fullMove: false } };
      } else {
        plan.moves.shift();
      }
    } else {
      plan.moves.shift();
    }
  }

  function ensureSplitId(el) {
    if (!el.getAttribute('data-split-id')) {
      splitIdCounter += 1;
      el.setAttribute('data-split-id', 's' + Date.now().toString(36) + '-' + splitIdCounter);
    }
    return el.getAttribute('data-split-id');
  }

  function makeContinuation(el) {
    var clone = el.cloneNode(false);
    clone.classList.add('resume-continued');
    clone.removeAttribute('data-split-id');
    clone.setAttribute('data-continues', ensureSplitId(el));
    var children = Array.prototype.slice.call(el.children);
    for (var i = 0; i < children.length; i++) {
      if (children[i].matches(CONT_TITLE_SEL)) {
        var t = children[i].cloneNode(true);
        t.setAttribute('data-continuation-title', 'true');
        var text = (t.textContent || '').replace(/\s+$/, '');
        if (text && text.indexOf('(continued)') === -1) {
          t.textContent = text + ' (continued)';
        }
        clone.appendChild(t);
        break;
      }
    }
    return clone;
  }

  function commitMoves(moves, target) {
    moves.forEach(function (m) {
      if (m.kind === 'whole') {
        target.appendChild(m.el);
      } else if (m.kind === 'partial') {
        var cont = makeContinuation(m.el);
        commitMoves(m.plan.moves, cont);
        target.appendChild(cont);
      } else if (m.kind === 'parallel') {
        var nonEmpty = m.cols.filter(function (c) {
          return c.plan.moves.length > 0;
        });
        if (nonEmpty.length > 1) {
          // Multiple columns continue: recreate the parallel container.
          var contC = makeContinuation(m.el);
          m.cols.forEach(function (c) {
            var colClone = makeContinuation(c.el);
            commitMoves(c.plan.moves, colClone);
            contC.appendChild(colClone);
          });
          target.appendChild(contC);
        } else if (nonEmpty.length === 1) {
          // Only one column continues: unwrap into normal full-width flow.
          var wrap = makeContinuation(nonEmpty[0].el);
          commitMoves(nonEmpty[0].plan.moves, wrap);
          target.appendChild(wrap);
        }
      }
    });
  }

  function commitPageParallel(cols, newPage) {
    var nonEmpty = cols.filter(function (c) {
      return c.plan.moves.length > 0;
    });
    if (nonEmpty.length > 1) {
      cols.forEach(function (c) {
        var colClone = makeContinuation(c.el);
        commitMoves(c.plan.moves, colClone);
        newPage.appendChild(colClone);
      });
    } else if (nonEmpty.length === 1) {
      var src = nonEmpty[0].el;
      var wrap = makeContinuation(src);
      wrap.classList.add(
        src.classList.contains('left-column') ? 'left-column-full' : 'right-column-full'
      );
      commitMoves(nonEmpty[0].plan.moves, wrap);
      newPage.appendChild(wrap);
    }
  }

  function buildNewPage(templatePage) {
    var p = templatePage.cloneNode(false);
    p.classList.add('resume-page-continued');
    p.removeAttribute('data-page');
    p.removeAttribute('data-split-id');
    var flow = getFlowRoot(templatePage);
    if (flow !== templatePage) {
      var flowClone = flow.cloneNode(false);
      flowClone.removeAttribute('data-split-id');
      p.appendChild(flowClone);
    }
    return p;
  }

  injectStyles();

  var pageEls = doc.querySelectorAll('.resume-page');
  if (!pageEls.length) return { paginated: false, pageCount: 0, reason: 'no-resume-page' };
  if (pageEls.length > 1) {
    return { paginated: true, pageCount: pageEls.length, reason: 'already-paginated' };
  }

  var pageHeightPx = measurePageHeight();
  var current = pageEls[0];
  var guard = 0;

  while (guard++ < MAX_PAGES) {
    var limit = pageBottomLimit(current, pageHeightPx);
    var newPage;

    if (isPageParallel(current)) {
      var leftCol = current.querySelector(':scope > .left-column');
      var rightCol = current.querySelector(':scope > .right-column');
      var cols = planColumns([leftCol, rightCol], limit, 0);
      var allFull = cols.every(function (c) {
        return c.plan.moves.length > 0 && c.plan.fullMove;
      });
      if (allFull) pinFirstLeaf(cols[0].plan);
      var any = cols.some(function (c) {
        return c.plan.moves.length > 0;
      });
      if (!any) break;
      newPage = buildNewPage(current);
      current.parentNode.insertBefore(newPage, current.nextSibling);
      commitPageParallel(cols, newPage);
    } else {
      var flowRoot = getFlowRoot(current);
      var plan = planContainer(flowRoot, limit, 0);
      if (plan.fullMove) pinFirstLeaf(plan);
      if (!plan.moves.length) break;
      newPage = buildNewPage(current);
      current.parentNode.insertBefore(newPage, current.nextSibling);
      commitMoves(plan.moves, getFlowRoot(newPage));
    }

    current = newPage;
  }

  // Number pages; only pages that actually fit get data-page (which enables
  // exact-height + overflow clipping in print). Oversized pages fall back to
  // the browser's native print flow.
  var finalPages = doc.querySelectorAll('.resume-page');
  Array.prototype.forEach.call(finalPages, function (pageEl, idx) {
    var rect = pageEl.getBoundingClientRect();
    if (rect.height <= pageHeightPx + TOL * 2) {
      pageEl.setAttribute('data-page', String(idx + 1));
    } else {
      pageEl.removeAttribute('data-page');
    }
  });

  return { paginated: finalPages.length > 1, pageCount: finalPages.length };
}

export function unwrapResumeDocument(doc) {
  var pages = Array.prototype.slice.call(doc.querySelectorAll('.resume-page'));
  if (pages.length <= 1) {
    if (pages.length === 1) pages[0].removeAttribute('data-page');
    return { unwrapped: false, pageCount: pages.length };
  }

  function getFlowRoot(pageEl) {
    return pageEl.querySelector(':scope > .content') || pageEl;
  }

  var base = pages[0];
  var baseFlow = getFlowRoot(base);

  // Move every element from continuation pages into the base flow, in order.
  for (var i = 1; i < pages.length; i++) {
    var flow = getFlowRoot(pages[i]);
    while (flow.firstChild) {
      baseFlow.appendChild(flow.firstChild);
    }
    pages[i].parentNode.removeChild(pages[i]);
  }

  // Merge continuation fragments into their originals. Process in REVERSE
  // document order: a page-3 fragment may continue a page-2 fragment, so the
  // deepest link of the chain must merge first while its target still exists.
  var guard = 0;
  while (guard++ < 100) {
    var conts = doc.querySelectorAll('[data-continues]');
    if (!conts.length) break;
    var cont = conts[conts.length - 1];
    var id = cont.getAttribute('data-continues');
    var target = doc.querySelector('[data-split-id="' + id + '"]');
    var titles = cont.querySelectorAll(':scope > [data-continuation-title]');
    Array.prototype.forEach.call(titles, function (t) {
      t.parentNode.removeChild(t);
    });
    if (target) {
      while (cont.firstChild) {
        target.appendChild(cont.firstChild);
      }
      cont.parentNode.removeChild(cont);
    } else {
      // Orphan continuation (source deleted by user edit): keep content in place.
      cont.removeAttribute('data-continues');
      cont.classList.remove('resume-continued', 'left-column-full', 'right-column-full');
    }
  }

  base.removeAttribute('data-page');
  base.classList.remove('resume-page-continued');
  return { unwrapped: true, pageCount: 1 };
}

/* ------------------------------------------------------------------ */
/* Browser helpers                                                     */
/* ------------------------------------------------------------------ */

const A4_WIDTH_PX = 794; // 210mm at 96dpi

function waitForAssets(doc, timeoutMs = 4000) {
  const fontsReady =
    doc.fonts && doc.fonts.ready ? doc.fonts.ready.catch(() => {}) : Promise.resolve();
  const imagesReady = Promise.all(
    Array.from(doc.images)
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise((resolve) => {
            img.onload = img.onerror = resolve;
          })
      )
  );
  const timeout = new Promise((resolve) => setTimeout(resolve, timeoutMs));
  return Promise.race([Promise.all([fontsReady, imagesReady]), timeout]);
}

/**
 * Repaginate a full HTML document string inside a hidden same-origin iframe.
 * Returns { html, pageCount }. On any failure the original HTML is returned.
 */
export async function paginateHtml(html) {
  if (!html || !html.includes('resume-page')) {
    return { html, pageCount: html ? 1 : 0 };
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${A4_WIDTH_PX + 40}px;height:1200px;visibility:hidden;pointer-events:none;`;
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    doc.open();
    doc.write(html);
    doc.close();

    await waitForAssets(doc);

    unwrapResumeDocument(doc);
    const result = paginateResumeDocument(doc, win);

    const serialized = '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
    return { html: serialized, pageCount: result.pageCount || 1 };
  } catch (err) {
    console.error('Preview pagination failed, using original HTML:', err);
    return { html, pageCount: countPages(html) };
  } finally {
    document.body.removeChild(iframe);
  }
}

function countPages(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Math.max(1, doc.querySelectorAll('.resume-page').length);
}

/**
 * Split a (paginated) document into standalone display fragments, one per page.
 * Each fragment carries every stylesheet from the source head so it renders
 * identically inside the preview's contenteditable container.
 */
export function extractPages(html) {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const pageEls = doc.querySelectorAll('.resume-page');

  const headAssets = [
    ...Array.from(doc.querySelectorAll('link[rel="stylesheet"]')).map((l) => l.outerHTML),
    ...Array.from(doc.querySelectorAll('style')).map((s) => s.outerHTML),
  ].join('\n');

  if (!pageEls.length) return [html];
  return Array.from(pageEls).map((page) => `${headAssets}\n${page.outerHTML}`);
}

/**
 * Replace the Nth .resume-page of a full document with the page contained in
 * an edited display fragment, returning the updated full document string.
 */
export function replacePageInHtml(fullHtml, pageIndex, editedFragment) {
  try {
    const doc = new DOMParser().parseFromString(fullHtml, 'text/html');
    const pages = doc.querySelectorAll('.resume-page');
    if (!pages.length || pageIndex >= pages.length) return fullHtml;

    const fragDoc = new DOMParser().parseFromString(editedFragment, 'text/html');
    const editedPage = fragDoc.querySelector('.resume-page');
    if (!editedPage) return fullHtml;

    pages[pageIndex].replaceWith(doc.importNode(editedPage, true));
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  } catch (err) {
    console.error('Failed to merge page edit:', err);
    return fullHtml;
  }
}
